<script lang="ts" setup>
import { useCoinRank } from "@/stores/coinRank";
import { CoinRankItem } from "@/types/coinRank";
import { Mate } from "@/types/entity";
import { computed, nextTick, onBeforeMount, ref } from "vue";
import { useRouter } from "vue-router";
import { ThreadListInstance } from "../ThreadList.vue";
import { useRouteQuery } from "@vueuse/router";
import { useMate } from "@/stores/mate";

const props = withDefaults(
  defineProps<{
    id?: number; // 榜单开始时间 - 用于定位榜单
    showTitle?: boolean;
    enablePullLoad?: boolean;
  }>(),
  {
    enablePullLoad: true,
  }
);
const coinRankStore = useCoinRank();
const router = useRouter();
const mateStore = useMate();

// States
const rankType = ref<"current" | "overall">("current");
const currentRankListRef = ref<ThreadListInstance | null>(null);
const overallRankListRef = ref<ThreadListInstance | null>(null);
const rankId = useRouteQuery<number | undefined>("id", undefined);

const currentRank = computed(() => {
  if (rankId.value) {
    return coinRankStore.getRankById(rankId.value);
  }
  return props.id
    ? coinRankStore.getRankById(props.id)
    : coinRankStore.currentCoinRank;
});
const currentRankItems = computed<CoinRankItem[]>(() =>
  rankId.value
    ? coinRankStore.uniCoinRankMap[rankId.value]?.coinRankItemzz || []
    : currentRank.value?.id
    ? coinRankStore.uniCoinRankMap[currentRank.value?.id]?.coinRankItemzz || []
    : []
);

const loading = ref(false);

// Lifecycle
const refreshRank = async (force = false) => {
  loading.value = true;
  try {
    if (rankId.value) {
      await coinRankStore.getOrFetchUniRank(rankId.value, force);
    } else {
      await coinRankStore.getOrFetchCurrentRank();
      await coinRankStore.getOrFetchUniRank(currentRank.value!.id, force);
    }
  } finally {
    loading.value = false;
  }
};
onBeforeMount(() => {
  refreshRank();
});

const goVoteList = (item: CoinRankItem) => {
  router.push({
    name: "missionDispatchRank",
    query: {
      mateUuid: item.mate.uuid,
      mateId: item.mate.id,
      coinRankId: item.coinRankId,
    },
  });
};

export interface RankHomeInstance {
  refreshRank: () => Promise<void>;
}

defineExpose<RankHomeInstance>({
  refreshRank: () => refreshRank(true),
});

const onPullLoad = async () => {
  await refreshRank(true);
};
</script>
<template>
  <div
    class="w-full grid overflow-hidden"
    :class="{ 'grid-rows-[auto_auto_1fr]': !rankId, 'grid-rows-[1fr]': rankId }"
  >
    <template v-if="!rankId">
      <div class="flex justify-between p-2 items-center pl-4 border-b">
        <div class="flex items-center">
          <button
            class="btn btn-xs rounded"
            :class="{ 'btn-primary': rankType === 'current' }"
            @click="rankType = 'current'"
          >
            {{ $t("rank.currentLeaderboard") }}
          </button>
          <div class="divider divider-horizontal m-0"></div>
          <button
            class="btn btn-xs rounded"
            :class="{ 'btn-primary': rankType === 'overall' }"
            @click="rankType = 'overall'"
          >
            {{ $t("rank.overallLeaderboard") }}
          </button>
        </div>
        <button
          class="btn btn-xs rounded gap-0"
          @click="router.push({ name: 'rankList' })"
        >
          {{ $t("rank.historyLeaderboard") }}
          <i class="iconify mingcute--right-line" />
        </button>
      </div>
    </template>
    <thread-list
      ref="currentRankListRef"
      class="rank-list"
      :enable-pull-load="enablePullLoad"
      :enable-scroll-load="false"
      :on-pull-load="onPullLoad"
      v-if="rankType === 'current'"
    >
      <div class="flex flex-col gap-0.5 min-h-[400px]">
        <div
          v-for="(item, idx) in currentRankItems"
          :key="item.id"
          class="cursor-pointer border-b relative"
          @click="goVoteList(item)"
        >
          <div class="grid grid-cols-[auto_1fr_auto] gap-2">
            <div
              class="text-base flex items-center justify-center font-bold w-10"
            >
              {{ item.position }}.
            </div>
            <div class="w-full overflow-hidden">
              <season-mate-rank-item
                :idx="item.position"
                :mate="item.mate"
                :vote-count="item.voteCount"
              />
            </div>
            <div
              :class="[
                ' flex  shrink-0 font-bold flex-col items-end text-right mr-2  ',
                idx === 0 ? 'justify-start' : 'justify-center',
              ]"
            >
              <span
                v-if="idx === 0"
                class="font-normal text-base-content/70 text-xs"
              >
                活跃值
              </span>
              {{ item.voteCount }}
            </div>
          </div>
        </div>
      </div>
    </thread-list>
    <thread-list
      ref="overallRankListRef"
      v-else-if="rankType === 'overall'"
      :on-pull-load="
        async () => {
          coinRankStore.overallNextCursor = undefined;
          await coinRankStore.getOrFetchOverallCoinRank(true);
          await nextTick();
          overallRankListRef?.resetScrollLoadingState();
        }
      "
      :on-scroll-load="
        async () => {
          if (coinRankStore.overallNextCursor === null) {
            return false;
          }
          await coinRankStore.fetchOlderOverallCoinRank();
          return true;
        }
      "
    >
      <div class="flex flex-col gap-0.5 min-h-[400px]">
        <div
          v-for="(item, idx) in coinRankStore.overallCoinRankList"
          :key="item.id"
          class="cursor-pointer border-b relative"
          @click="mateStore.toMateHome(item.entity as Mate)"
        >
          <div class="grid grid-cols-[auto_1fr_auto] gap-2">
            <div
              class="text-base flex items-center justify-center font-bold w-10"
            >
              {{ item.position }}.
            </div>
            <div class="w-full overflow-hidden">
              <season-mate-rank-item
                :idx="item.position"
                :mate="item.entity! as Mate"
                :vote-count="item.voteCount"
              />
            </div>
            <div
              :class="[
                ' flex  shrink-0 font-bold flex-col items-end text-right mr-2  ',
                idx === 0 ? 'justify-start' : 'justify-center',
              ]"
            >
              <span
                v-if="idx === 0"
                class="font-normal text-base-content/70 text-xs"
              >
                活跃值
              </span>
              {{ item.voteCount }}
            </div>
          </div>
        </div>
      </div>
    </thread-list>
  </div>
</template>
<style>
.rank-home-tabs .van-tabs__nav {
  @apply bg-neutral pb-3;
}
</style>
